import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
} from '@mui/material';
import {LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';
import {useEffect, useMemo, useRef, useState} from 'react';

import {DateTimeSelect, DateTimeSelectHandle} from './DateTimeSelect';

const SOFT_WARNING_DAYS = 90;

type Props = {
  labels?: [string, string];
  value?: [Date | null, Date | null];
  disabled?: boolean;
  disabledStart?: boolean;
  disabledEnd?: boolean;
  size?: 'small' | 'medium';
  maxDays?: number;
  maxDate?: Date | null;
  onChange?: (value: [Date | null, Date | null]) => any;
};

export const DateTimeRangeSelect = ({
  labels,
  value,
  disabled,
  disabledStart,
  disabledEnd,
  size,
  maxDays,
  maxDate,
  onChange,
}: Props) => {
  const hasHardLimit = maxDays != null;

  const initialStartDate = useMemo(
    () => dayjs(value?.[0] ?? undefined).valueOf(),
    [value?.[0]]
  );
  const initialEndDate = useMemo(
    () => dayjs(value?.[1] ?? undefined).valueOf(),
    [value?.[1]]
  );

  const [startDate, setStartDate] = useState<number | null>(initialStartDate);
  const [endDate, setEndDate] = useState<number | null>(initialEndDate);

  const days = useMemo(
    () => dayjs(endDate ?? undefined).diff(startDate ?? undefined, 'day'),
    [startDate, endDate]
  );

  const orderingInvalid = useMemo(
    () => startDate != null && endDate != null && endDate < startDate,
    [startDate, endDate]
  );
  const hardLimitExceeded = useMemo(
    () => hasHardLimit && days > (maxDays ?? 0),
    [hasHardLimit, days, maxDays]
  );
  const needsConfirm = useMemo(
    () => !hasHardLimit && days > SOFT_WARNING_DAYS,
    [hasHardLimit, days]
  );

  // Track which picker was last edited so we know which one to auto-open
  const lastEditedRef = useRef<'start' | 'end' | null>(null);

  // Imperative handles so we can programmatically open the other picker
  const startRef = useRef<DateTimeSelectHandle>(null);
  const endRef = useRef<DateTimeSelectHandle>(null);

  const openOther = () => {
    if (lastEditedRef.current === 'start') {
      endRef.current?.open();
    } else {
      startRef.current?.open();
    }
  };

  // Confirm dialog state for large-range soft warning
  const [confirmOpen, setConfirmOpen] = useState(false);
  const confirmedKeyRef = useRef<string | null>(null);

  const fireOnChange = (start: number | null, end: number | null) => {
    const newValue: [Date | null, Date | null] = [
      dayjs(start).toDate(),
      dayjs(end).toDate(),
    ];
    if (!isEqual(value, newValue)) {
      onChange?.(newValue);
    }
  };

  useEffect(() => {
    setStartDate(initialStartDate);
    setEndDate(initialEndDate);
  }, [initialStartDate, initialEndDate]);

  useEffect(() => {
    if (startDate == null || endDate == null) {
      return;
    }

    if (orderingInvalid || hardLimitExceeded) {
      openOther();
      return;
    }

    const key = `${startDate}:${endDate}`;
    if (needsConfirm && confirmedKeyRef.current !== key) {
      setConfirmOpen(true);
      return;
    }

    fireOnChange(startDate, endDate);
  }, [startDate, endDate]);

  const handleCancel = () => {
    setConfirmOpen(false);
    setStartDate(initialStartDate);
    setEndDate(initialEndDate);
  };

  const handleContinue = () => {
    const key = `${startDate}:${endDate}`;
    confirmedKeyRef.current = key;
    setConfirmOpen(false);
    fireOnChange(startDate, endDate);
  };

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <DateTimeSelect
              ref={startRef}
              value={startDate != null ? dayjs(startDate).toDate() : null}
              disabled={disabled || disabledStart}
              label={labels?.[0] ?? 'Start time'}
              error={orderingInvalid || hardLimitExceeded}
              renderInput={{
                size,
                fullWidth: true,
              }}
              maxDate={maxDate}
              onChange={(v) => {
                lastEditedRef.current = 'start';
                setStartDate(v?.valueOf() ?? null);
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <DateTimeSelect
              ref={endRef}
              value={endDate != null ? dayjs(endDate).toDate() : null}
              disabled={disabled || disabledEnd}
              renderInput={{
                size,
                fullWidth: true,
              }}
              label={labels?.[1] ?? 'End time'}
              error={orderingInvalid || hardLimitExceeded}
              maxDate={maxDate}
              onChange={(v) => {
                lastEditedRef.current = 'end';
                setEndDate(v?.valueOf() ?? null);
              }}
            />
          </Grid>
        </Grid>
      </LocalizationProvider>

      <Dialog open={confirmOpen} onClose={handleCancel}>
        <DialogTitle>Large date range</DialogTitle>

        <DialogContent>
          <DialogContentText>
            {days} days selected. Data may take a long time to load.
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleContinue}>Continue</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
